import { Document, Page, Text, View, Font } from '@react-pdf/renderer';
import { ILead, ILeadReq } from '../models/BaseModel';
import styles from './InvoiceStyle';
import { format } from 'date-fns';
import ResortInfo from './ResortInfo';
import { IAParamValue } from '../components/myLead/requirements/ActivityReq';

Font.register({
    family: 'Open Sans',
    fonts: [
        { src: 'https://fonts.gstatic.com/s/opensans/v27/mem8YaGs126MiZpBA-UFVZ0e.ttf' },
    ],
});

interface Iprops {
    lead: ILead
    reqs: ILeadReq[],
}


const InvoiceDocument: React.FC<Iprops> = ({ lead, reqs }) => {
    let allTotal = 0;

    let roomsTotalPrice = 0;
    let boatingsTotalPrice = 0;
    let pickAndDropTotalPrice = 0;
    let vehiclesTotalPrice = 0;
    let foodPackageTotalPrice = 0;
    let activitiesTotalPrice = 0;

    function calculateDayNightCount(start: Date, end: Date): { dayCount: number, nightCount: number } {
        const startTime = start.getTime();
        const endTime = end.getTime();

        if (endTime <= startTime) return { dayCount: 0, nightCount: 0 };

        let current = new Date(start);
        let dayCount = 0;
        let nightCount = 0;

        while (current.getTime() < endTime) {
            const hour = current.getHours();
            const isDayTime = hour >= 6 && hour < 18;
            if (isDayTime) {
                dayCount++;
            } else {
                nightCount++;
            }
            current = new Date(current.getTime() + 12 * 60 * 60 * 1000);
        }

        return { dayCount, nightCount };
    }

    function getUnitNeeded(adults: number, children: number, occupancy: number): number {
        if (occupancy <= 0) {
            return 0;
        }
        return Math.ceil((adults + children) / occupancy);
    }

    return (
        <Document>
            <Page style={styles.page}>
                <ResortInfo />
                {reqs.map((req, i) => <View key={req.id}>
                    <Text style={styles.heading}>{i + 1}. Destination: {req.cityName}, {req.stateName}, {req.countryName}</Text>

                    <View style={styles.section}>
                        <View style={styles.tableHeader}>
                            <Text style={styles.columnLeft}>Contact</Text>
                            <Text style={styles.columnLeft}>{lead.contactName} | {lead.contactContact}</Text>
                        </View>
                        <View style={styles.tableRow}>
                            <Text style={styles.columnLeft}>Purpose</Text>
                            <Text style={styles.columnLeft}>{req.purposeOfTripName}</Text>
                        </View>
                        <View style={styles.tableRow}>
                            <Text style={styles.columnLeft}>Package</Text>
                            <Text style={styles.columnLeft}>{req.packageName}</Text>
                        </View>
                        <View style={styles.tableRow}>
                            <Text style={styles.columnLeft}>Check-in</Text>
                            <Text style={styles.columnLeft}>{req.checkin ? format(new Date(req.checkin), 'd MMMM yyyy (EEEE)') : '-'}</Text>
                        </View>
                        <View style={styles.tableRow}>
                            <Text style={styles.columnLeft}>Check-out</Text>
                            <Text style={styles.columnLeft}>{req.checkout ? format(new Date(req.checkout), 'd MMMM yyyy (EEEE)') : '-'}</Text>
                        </View>
                        <View style={styles.tableRow}>
                            <Text style={styles.columnLeft}>Hotel</Text>
                            <Text style={styles.columnLeft}>{req.hotelName}</Text>
                        </View>
                        <View style={styles.tableRow}>
                            <Text style={styles.columnLeft}>MealPlan</Text>
                            <Text style={styles.columnLeft}>{req.mealPlan}</Text>
                        </View>
                        <View style={styles.tableRow}>
                            <Text style={styles.columnLeft}>Adults</Text>
                            <Text style={styles.columnLeft}>{req.noOfAdults}</Text>
                        </View>
                        <View style={styles.tableRow}>
                            <Text style={styles.columnLeft}>Children (7 to 12 years)</Text>
                            <Text style={styles.columnLeft}>{req.noOfChildren}</Text>
                        </View>
                    </View>

                    <View style={styles.section}>
                        <Text style={{ marginBottom: 10 }}>Room :</Text>
                        <View style={styles.tableHeader}>
                            <Text style={styles.columnTitle}>Room Type</Text>
                            <Text style={styles.columnRight}>Price/Day</Text>
                            <Text style={styles.columnRight}>Quantity</Text>
                            <Text style={styles.columnRight}>Mattress</Text>
                            <Text style={styles.columnRight}>Total</Text>
                        </View>

                        {req.rooms?.length && req.rooms.map((room, index) => {
                            const duration = (req.packageDuration ?? 24) / 24;
                            const price = ((room.mattress * room.mattressPrice) + (room.count * room.price)) * duration;
                            roomsTotalPrice += price;
                            return <View style={styles.tableRow} key={index}>
                                <Text style={styles.columnTitle}>{room.roomTypeName}</Text>
                                <Text style={styles.columnRight}>{room.price} * {duration}</Text>
                                <Text style={styles.columnRight}>{room.count}</Text>
                                <Text style={styles.columnRight}>{room.mattress ? `${room.mattress} * ${room.mattressPrice} * ${duration}` : 0}</Text>
                                <Text style={styles.columnRight}>{price.toFixed(2)}</Text>
                            </View>
                        })}
                        <View style={styles.tableRow}>
                            <Text style={styles.columnLeft}> </Text>
                        </View>
                        <View style={styles.tableRow}>
                            <Text style={styles.columnLeft}>Total :</Text>
                            <Text style={styles.columnRight}>{roomsTotalPrice.toFixed(2)}</Text>
                        </View>
                        <View style={styles.tableRow}>
                            <Text style={styles.columnLeft}>GST (12%) :</Text>
                            <Text style={styles.columnRight}>{((roomsTotalPrice * 12) / 100).toFixed(2)}</Text>
                        </View>
                        <View style={styles.tableRow}>
                            <Text style={styles.columnLeft}>Total (+GST) :</Text>
                            <Text style={styles.columnRight}>{(() => (roomsTotalPrice += ((roomsTotalPrice * 12) / 100)))().toFixed(2)}</Text>
                        </View>
                    </View>

                    {(req.pickAndDrops && req.pickAndDrops.length > 0) &&
                        <View style={styles.section}>
                            <Text style={{ marginBottom: 10 }}>Transport :</Text>
                            {(req.pickAndDrops && req.pickAndDrops.length > 0) && <>
                                {req.pickAndDrops.map((pickAndDrop, index) => {
                                    pickAndDropTotalPrice += vehiclesTotalPrice;
                                    vehiclesTotalPrice = 0;
                                    const location: { name: string, distance: number } = { name: pickAndDrop.pickupLocationName ?? '', distance: pickAndDrop.distance };
                                    return <View key={index} style={{ marginVertical: 10 }}>
                                        <Text style={{ paddingVertical: 5 }}>
                                            {pickAndDrop.tripType.toUpperCase()} | {pickAndDrop.time && format(new Date(pickAndDrop.time), 'd MMMM hh:mm a')}{pickAndDrop.tripType === 'roundtrip' && pickAndDrop.endtime && ' - ' + format(new Date(pickAndDrop.endtime), 'd MMMM hh:mm a')} | {pickAndDrop.noOfPeople} Passengers | {location.name} | {location.distance} Km
                                        </Text>
                                        <View>
                                            <View style={styles.tableHeader}>
                                                <Text style={styles.columnTitle2}>Vehicle Type</Text>
                                                <Text style={styles.columnRight}>Day</Text>
                                                <Text style={styles.columnRight}>Night</Text>
                                                <Text style={{ ...styles.columnRight, ...styles.columnTitle2 }}>Price/Km</Text>
                                                <Text style={styles.columnRight}>Parking</Text>
                                                <Text style={styles.columnRight}>Driver</Text>
                                                <Text style={styles.columnRight}>Quantity</Text>
                                                <Text style={styles.columnRight}>Total</Text>
                                            </View>

                                            {pickAndDrop.vehicles?.length && pickAndDrop.vehicles.map((vehicle, index) => {
                                                let price = 0;
                                                const distanceCharge = pickAndDrop.tripType === 'roundtrip'
                                                    ? vehicle.ratePerKm * location.distance * 2
                                                    : vehicle.ratePerKm * location.distance;
                                                let dayCount = 0;
                                                let nightCount = 0;
                                                let dayCharge = 0;
                                                let nightCharge = 0;
                                                if (pickAndDrop.tripType === 'roundtrip' && pickAndDrop.time && pickAndDrop.endtime) {
                                                    const result = calculateDayNightCount(new Date(pickAndDrop.time), new Date(pickAndDrop.endtime));
                                                    dayCount = result.dayCount;
                                                    nightCount = result.nightCount;
                                                    dayCharge = vehicle.dayCharge * dayCount;
                                                    nightCharge = vehicle.nightCharge * nightCount;
                                                } else {
                                                    const hour = pickAndDrop.time ? new Date(pickAndDrop.time).getHours() : 12;
                                                    const isDay = hour >= 6 && hour < 18;
                                                    dayCharge = isDay ? vehicle.dayCharge : 0;
                                                    nightCharge = !isDay ? vehicle.nightCharge : 0;
                                                    dayCount = isDay ? 1 : 0;
                                                    nightCount = !isDay ? 1 : 0;
                                                }
                                                price = vehicle.count * (distanceCharge + vehicle.parkingCharge + dayCharge + nightCharge + vehicle.driverCharge);
                                                vehiclesTotalPrice += price;

                                                return <View style={styles.tableRow} key={index}>
                                                    <Text style={styles.columnTitle2}>{vehicle.vehicleTypeName}</Text>
                                                    <Text style={styles.columnRight}>{dayCount ? `${vehicle.dayCharge} * ${dayCount}` : 0}</Text>
                                                    <Text style={styles.columnRight}>{nightCount ? `${vehicle.nightCharge} * ${nightCount}` : 0}</Text>
                                                    {pickAndDrop.tripType === 'roundtrip' ? <Text style={{ ...styles.columnRight, ...styles.columnTitle2 }}>{vehicle.ratePerKm} * {location.distance} * 2</Text> :
                                                        <Text style={{ ...styles.columnRight, ...styles.columnTitle2 }}>{vehicle.ratePerKm} * {location.distance}</Text>}
                                                    <Text style={styles.columnRight}>{vehicle.parkingCharge}</Text>
                                                    <Text style={styles.columnRight}>{vehicle.driverCharge}</Text>
                                                    <Text style={styles.columnRight}>{vehicle.count}</Text>
                                                    <Text style={styles.columnRight}>{price.toFixed(2)}</Text>
                                                </View>;
                                            })}
                                            <View style={styles.tableRow}>
                                                <Text style={styles.columnTitle2}></Text>
                                                <Text style={styles.columnRight}></Text>
                                                <Text style={styles.columnRight}></Text>
                                                <Text style={{ ...styles.columnRight, ...styles.columnTitle2 }}></Text>
                                                <Text style={styles.columnRight}></Text>
                                                <Text style={styles.columnRight}></Text>
                                                <Text style={styles.columnRight}></Text>
                                                <Text style={styles.columnRight}>{vehiclesTotalPrice.toFixed(2)}</Text>
                                            </View>
                                        </View>
                                    </View>
                                })}
                                {(() => {
                                    pickAndDropTotalPrice += vehiclesTotalPrice;
                                    return <><View style={styles.tableRow}>
                                        <Text style={styles.columnLeft}> </Text>
                                    </View>
                                        <View style={styles.tableRow}>
                                            <Text style={styles.columnLeft}>Total :</Text>
                                            <Text style={styles.columnRight}>{pickAndDropTotalPrice.toFixed(2)}</Text>
                                        </View></>
                                })()}
                            </>}
                        </View>
                    }

                    <View style={styles.section}>
                        {/* <Text style={styles.heading}>{at.activityTypeName} :</Text> */}
                        <Text style={{ marginBottom: 10 }}>Activities :</Text>
                        <View style={styles.tableHeader}>
                            <Text style={styles.columnTitle}>Activity</Text>
                            <Text style={styles.columnRight}>Occupancy</Text>
                            <Text style={styles.columnRight}>Price</Text>
                            <Text style={styles.columnRight}>People</Text>
                            <Text style={styles.columnRight}>Total</Text>
                        </View>
                        {req.activityTypes && req.activityTypes.map(at => {
                            const groupedByAcivityId = at.reqParamsValue?.reduce((acc: Record<number, IAParamValue[]>, item) => {
                                if (!acc[item.activityId]) acc[item.activityId] = [];
                                acc[item.activityId].push(item);
                                return acc;
                            }, {});
                            return <View key={at.activityTypeId}>
                                {groupedByAcivityId && Object.keys(groupedByAcivityId).map((activityIdStr, i) => {
                                    const activityId = parseInt(activityIdStr);
                                    const apv = at.activityParamsValue?.find(a => a.activityId === activityId)?.paramsValue;
                                    const occupancy = Number(apv?.find(p => p.name === 'Occupancy')?.value);
                                    const price = Number(apv?.find(p => p.name === 'Price')?.value);
                                    const adults = Number(at.reqParamsValue?.find(a => a.activityId === activityId && a.name === 'Adults')?.value);
                                    const children = Number(at.reqParamsValue?.find(a => a.activityId === activityId && a.name === 'Children')?.value);

                                    const total = getUnitNeeded(adults, children, occupancy) * price;
                                    activitiesTotalPrice += total;

                                    return <View style={styles.tableRow} key={i}>
                                        <Text style={styles.columnTitle}>{req.itinerary?.find(f => f.activityId === activityId)?.name}</Text>
                                        <Text style={styles.columnRight}>{occupancy}</Text>
                                        <Text style={styles.columnRight}>{price}</Text>
                                        <Text style={styles.columnRight}>{adults + children}</Text>
                                        <Text style={styles.columnRight}>{total.toFixed(2)}</Text>
                                    </View>
                                })}
                            </View>
                        })}
                        <View style={styles.tableRow}>
                            <Text style={styles.columnLeft}> </Text>
                        </View>
                        <View style={styles.tableRow}>
                            <Text style={styles.columnLeft}>Total :</Text>
                            <Text style={styles.columnRight}>{activitiesTotalPrice.toFixed(2)}</Text>
                        </View>
                        <View style={styles.tableRow}>
                            <Text style={styles.columnLeft}>GST (18%) :</Text>
                            <Text style={styles.columnRight}>{((activitiesTotalPrice * 18) / 100).toFixed(2)}</Text>
                        </View>
                        <View style={styles.tableRow}>
                            <Text style={styles.columnLeft}>Total (+GST) :</Text>
                            <Text style={styles.columnRight}>{(() => (activitiesTotalPrice += ((activitiesTotalPrice * 18) / 100)))().toFixed(2)}</Text>
                        </View>
                    </View>

                    {(() => {
                        const final = (roomsTotalPrice + boatingsTotalPrice + pickAndDropTotalPrice + foodPackageTotalPrice + activitiesTotalPrice);
                        allTotal += final;
                        roomsTotalPrice = 0;
                        boatingsTotalPrice = 0;
                        pickAndDropTotalPrice = 0;
                        vehiclesTotalPrice = 0;
                        foodPackageTotalPrice = 0;
                        activitiesTotalPrice = 0;
                        return <View style={styles.totalContainer}>
                            <View style={styles.total}>
                                <Text>Total (+GST) : </Text>
                                <Text style={{ color: 'rgb(200, 0, 0)' }}>{(final).toFixed(2)}</Text>
                                <Text> Rs.</Text>
                            </View>
                        </View>
                    })()}
                </View>)}
                {(() => {
                    return <View style={{ ...styles.totalContainer, marginTop: 20 }}>
                        <View style={styles.total}>
                            <Text>All Total (+GST) : </Text>
                            <Text style={{ color: 'rgb(200, 0, 0)' }}>{(allTotal).toFixed(2)}</Text>
                            <Text> Rs.</Text>
                        </View>
                    </View>
                })()}
                <ResortInfo />
            </Page>
        </Document>
    );
};

export default InvoiceDocument;
